import { useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Reusable "up to 10 short statements" editor, used for both Brand Voice
 * (dna_statements) and Visual Identity (visual_identity_statements).
 * Statements are persisted on the brand and feed downstream prompts.
 */
export default function StatementsPanel({
  brandId,
  canManage,
  title,
  description,
  fieldKey,
  saveKey,
  endpoint,
  testidPrefix,
  placeholder,
  saveLabel,
}) {
  const [statements, setStatements] = useState([]);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/brands/${brandId}`);
        setStatements(data[fieldKey] || []);
      } catch (e) {
        setError(e.response?.data?.detail || `Could not load ${title}`);
      }
    })();
  }, [brandId, fieldKey, title]);

  const setAt = (i, v) => setStatements((p) => p.map((s, idx) => (idx === i ? v : s)));
  const addRow = () => setStatements((p) => (p.length >= 10 ? p : [...p, ""]));
  const removeAt = (i) => setStatements((p) => p.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data } = await api.put(endpoint, { [saveKey]: statements });
      setStatements(data[saveKey]);
      setSavedAt(new Date().toISOString());
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid={`${testidPrefix}-panel`}>
      <h2 className="font-serif text-2xl">{title}</h2>
      <p className="text-sm text-[#3D3A35] mt-2 max-w-2xl">{description}</p>
      {error && <p className="text-sm text-[#B5462F] mt-3">{error}</p>}

      <div className="mt-5 grid grid-cols-1 gap-3 max-w-2xl">
        {statements.length === 0 && (
          <p className="text-[#6B6358] text-sm italic">
            No statements yet. Click &ldquo;Add a statement&rdquo; to start.
          </p>
        )}
        {statements.map((s, i) => (
          <div key={i} data-testid={`${testidPrefix}-row-${i}`} className="flex gap-2 items-start">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358] mt-2 w-5 text-right">
              {String(i + 1).padStart(2, "0")}
            </span>
            <textarea
              data-testid={`${testidPrefix}-input-${i}`}
              rows={2}
              maxLength={200}
              value={s}
              disabled={!canManage}
              onChange={(e) => setAt(i, e.target.value)}
              placeholder={placeholder}
              className="flex-1 px-3 py-2 border border-[#1A1F2C]/20 rounded-md text-sm bg-white disabled:opacity-60"
            />
            {canManage && (
              <button
                type="button"
                data-testid={`${testidPrefix}-row-${i}-remove`}
                onClick={() => removeAt(i)}
                className="px-2 py-1 text-xs text-[#6B6358] hover:text-[#B5462F]"
                title="Remove this statement"
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      {canManage && (
        <div className="mt-4 flex flex-wrap gap-3 items-center">
          <button
            type="button"
            data-testid={`${testidPrefix}-add`}
            onClick={addRow}
            disabled={statements.length >= 10}
            className="px-4 py-2 text-xs uppercase tracking-[0.18em] border border-[#1A1F2C]/30 rounded-full text-[#3D3A35] disabled:opacity-50"
          >
            + Add a statement
          </button>
          <button
            type="button"
            data-testid={`${testidPrefix}-save`}
            onClick={save}
            disabled={saving}
            className="px-5 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em] font-medium disabled:opacity-60"
          >
            {saving ? "Saving…" : saveLabel}
          </button>
          {savedAt && (
            <span className="text-[11px] text-[#6B6358]">
              Saved {new Date(savedAt).toLocaleTimeString()}
            </span>
          )}
        </div>
      )}
    </section>
  );
}
